import { useEffect, useState } from "react";
import { DataTable } from "../../components/DataTable";
import { useAuth } from "../../context/AuthContext";
import { StoneType } from "../../interfaces/StoneType";
import { stoneTypeService } from "../../services/stoneTypeService";

// =====================================================
// DETAIL PANEL
// =====================================================

function DetailPanel({
  item,
  onClose,
  onSaved,
  onDeleted,
  canManage,
}: {
  item: StoneType;
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
  canManage: boolean;
}) {
  const [form, setForm] = useState<StoneType>({ ...item });
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  function handleChange(field: keyof StoneType, value: any) {
    const updated = { ...form, [field]: value };

    if (field === "stoneTypeName") {
      updated.stoneTypeSlug = value
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "");
    }

    setForm(updated);
  }

  async function handleSave() {
    try {
      setSaving(true);

      await stoneTypeService.update(form);

      setSuccessMsg("Updated Successfully");

      onSaved();

    } catch (error) {

      console.error(error);

      setSuccessMsg("Update failed");

    } finally {

      setSaving(false);
    }
  }

  async function handleDelete() {

    try {

      await stoneTypeService.delete(form.stoneTypeID);

      setSuccessMsg("Deleted Successfully");

      onDeleted();

      setTimeout(() => onClose(), 1200);

    } catch (error) {

      console.error(error);

      setSuccessMsg("Delete failed");
    }
  }

  return (
    <div
      className="mty-panel-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="mty-panel">

        {/* HEADER */}
        <div className="mty-panel-header">
          <div>
            <h5 className="fw-bold mb-0">
              Stone Type Details
            </h5>

            <small>
              View / Edit Stone Type
            </small>
          </div>

          <button
            className="btn-close"
            onClick={onClose}
          />
        </div>

        {/* BODY */}
        <div className="mty-panel-body">

          <div className="row g-3">

            <div className="col-md-6">
              <label className="form-label">
                Stone Type Name
              </label>

              <input
                className="form-control"
                value={form.stoneTypeName || ""}
                disabled={!canManage}
                onChange={(e) =>
                  handleChange(
                    "stoneTypeName",
                    e.target.value
                  )
                }
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">
                Slug
              </label>

              <input
                className="form-control"
                value={form.stoneTypeSlug || ""}
                disabled={!canManage}
                onChange={(e) =>
                  handleChange(
                    "stoneTypeSlug",
                    e.target.value
                  )
                }
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">
                Status
              </label>

              <select
                className="form-select"
                disabled={!canManage}
                value={form.status || "Active"}
                onChange={(e) =>
                  handleChange(
                    "status",
                    e.target.value
                  )
                }
              >
                <option value="Active">
                  Active
                </option>

                <option value="Hidden">
                  Hidden
                </option>
              </select>
            </div>

            <div className="col-12">
              <label className="form-label">
                Description
              </label>

              <textarea
                rows={3}
                className="form-control"
                disabled={!canManage}
                value={form.description || ""}
                onChange={(e) =>
                  handleChange(
                    "description",
                    e.target.value
                  )
                }
              />
            </div>

            <div className="col-12 mt-3">
              <h6 className="fw-bold">
                SEO Settings
              </h6>
            </div>

            <div className="col-md-6">
              <label className="form-label">
                Meta Title
              </label>

              <input
                className="form-control"
                disabled={!canManage}
                value={form.metaTitle || ""}
                onChange={(e) =>
                  handleChange(
                    "metaTitle",
                    e.target.value
                  )
                }
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">
                Meta Keywords
              </label>

              <input
                className="form-control"
                disabled={!canManage}
                value={form.metaKeywords || ""}
                onChange={(e) =>
                  handleChange(
                    "metaKeywords",
                    e.target.value
                  )
                }
              />
            </div>

            <div className="col-12">
              <label className="form-label">
                Meta Description
              </label>

              <textarea
                rows={2}
                className="form-control"
                disabled={!canManage}
                value={form.metaDescription || ""}
                onChange={(e) =>
                  handleChange(
                    "metaDescription",
                    e.target.value
                  )
                }
              />
            </div>
          </div>
        </div>

        {/* FOOTER */}
        {canManage && (
          <div className="mty-panel-footer d-flex justify-content-between">

            <button
              className="btn btn-danger"
              onClick={handleDelete}
              disabled={saving}
            >
              Delete
            </button>

            <div className="d-flex gap-2">

              <button
                className="btn btn-secondary"
                onClick={onClose}
              >
                Cancel
              </button>

              <button
                className="btn btn-primary"
                disabled={saving}
                onClick={handleSave}
              >
                {saving
                  ? "Updating..."
                  : "Update"}
              </button>

            </div>
          </div>
        )}
      </div>

      {/* SUCCESS MODAL */}
      {successMsg && (
        <div
          className="pm-del-overlay"
          style={{ zIndex: 1100 }}
          onClick={() => {
            setSuccessMsg(null);
            onClose();
          }}
        >
          <div className="pm-del-box">

            <div
              className="pm-del-ico"
              style={{ background: "#f0fdf4" }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#22c55e"
                strokeWidth="2.5"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>

            <div
              className="pm-del-title"
              style={{ color: "#166534" }}
            >
              {successMsg}
            </div>

            <div className="pm-del-btns">
              <button
                className="pm-btn-ghost"
                onClick={() => {
                  setSuccessMsg(null);
                  onClose();
                }}
                type="button"
              >
                OK
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}

// =====================================================
// MAIN PAGE
// =====================================================

export function CreateStoneTypePage() {

  const { user } = useAuth();

  const canManage = true;

  const [loading, setLoading] = useState(false);

  const [items, setItems] =
    useState<StoneType[]>([]);

  const [selected, setSelected] =
    useState<StoneType | null>(null);

  const [deleteConfirm, setDeleteConfirm] =
    useState<StoneType | null>(null);

  const [deleteSuccessMsg, setDeleteSuccessMsg] =
    useState<string | null>(null);

  const [deleting, setDeleting] =
    useState(false);

  const [infoModal, setInfoModal] =
    useState<{
      msg: string;
      type: "success" | "error" | "warn";
    } | null>(null);

  const emptyForm = (): Partial<StoneType> => ({
    stoneTypeName: "",
    stoneTypeSlug: "",
    description: "",
    status: "Active",

    metaTitle: "",
    metaDescription: "",
    metaKeywords: "",

    canonicalUrl: "",
    ogTitle: "",
    ogDescription: "",
    ogImage: "",
  });

  const [formData, setFormData] =
    useState<Partial<StoneType>>(emptyForm());

  // =====================================================
  // LOAD
  // =====================================================

  async function loadAll() {

    try {

      setLoading(true);

      const response =
        await stoneTypeService.getAll();

      setItems(response.dataList || []);

    } catch (error) {

      console.error(error);

    } finally {

      setLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
  }, []);

  // =====================================================
  // FORM HANDLERS
  // =====================================================

  function handleChange(name: string, value: any) {

    const updated = {
      ...formData,
      [name]: value,
    };

    if (name === "stoneTypeName") {

      updated.stoneTypeSlug = value
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "");
    }

    setFormData(updated);
  }

  async function handleSubmit() {

    try {

      if (!formData.stoneTypeName)
        return;

      const response =
        await stoneTypeService.create({

          stoneTypeName:
            formData.stoneTypeName || "",

          stoneTypeSlug:
            formData.stoneTypeSlug || "",

          description:
            formData.description || "",

          status:
            formData.status || "Active",

          metaTitle:
            formData.metaTitle || "",

          metaDescription:
            formData.metaDescription || "",

          metaKeywords:
            formData.metaKeywords || "",

          canonicalUrl:
            formData.canonicalUrl || "",

          ogTitle:
            formData.ogTitle || "",

          ogDescription:
            formData.ogDescription || "",

          ogImage:
            formData.ogImage || "",

          schemaMarkup: "",
        });

      setInfoModal({
        msg:
          response.message ||
          "Inserted Successfully",

        type: "success",
      });

      setFormData(emptyForm());

      await loadAll();

    } catch (error: any) {

      console.error(error);

      setInfoModal({
        msg:
          error?.response?.data?.message ||
          "Insert failed",

        type: "error",
      });
    }
  }

  // =====================================================
  // DELETE
  // =====================================================

  async function doDelete() {

    if (!deleteConfirm) return;

    try {

      setDeleting(true);

      const response =
        await stoneTypeService.delete(
          deleteConfirm.stoneTypeID
        );

      setDeleteSuccessMsg(
        response.message ||
        "Deleted Successfully"
      );

      setItems((prev) =>
        prev.filter(
          (x) =>
            x.stoneTypeID !==
            deleteConfirm.stoneTypeID
        )
      );

      setDeleteConfirm(null);

      setSelected(null);

    } catch (error: any) {

      console.error(error);

      setDeleteSuccessMsg(
        error?.response?.data?.message ||
        "Delete failed"
      );

    } finally {

      setDeleting(false);
    }
  }

  return (
    <>
      <div className="d-grid gap-4">

        {/* FORM CARD */}
        <div
          className="card border-0 shadow-sm"
          style={{ borderRadius: 16 }}
        >
          <div className="card-body p-4">

            <div className="d-flex align-items-center gap-2 mb-4">

              <div className="mst-icon-box">
                <i className="bi bi-gem"></i>
              </div>

              <div>
                <h5 className="fw-bold mb-0">
                  Create Stone Type
                </h5>

                <small>
                  Stone Type Management
                </small>
              </div>
            </div>

            <div className="row g-3">

              <div className="col-md-6">
                <label className="form-label">
                  Stone Type Name
                </label>

                <input
                  className="form-control"
                  value={formData.stoneTypeName || ""}
                  onChange={(e) =>
                    handleChange(
                      "stoneTypeName",
                      e.target.value
                    )
                  }
                  placeholder="e.g. Diamond, Ruby, Emerald"
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">
                  Slug
                </label>

                <input
                  className="form-control"
                  value={formData.stoneTypeSlug || ""}
                  onChange={(e) =>
                    handleChange(
                      "stoneTypeSlug",
                      e.target.value
                    )
                  }
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">
                  Status
                </label>

                <select
                  className="form-select"
                  value={formData.status || "Active"}
                  onChange={(e) =>
                    handleChange(
                      "status",
                      e.target.value
                    )
                  }
                >
                  <option value="Active">
                    Active
                  </option>

                  <option value="Hidden">
                    Hidden
                  </option>
                </select>
              </div>

              <div className="col-12">
                <label className="form-label">
                  Description
                </label>

                <textarea
                  rows={3}
                  className="form-control"
                  value={formData.description || ""}
                  onChange={(e) =>
                    handleChange(
                      "description",
                      e.target.value
                    )
                  }
                />
              </div>
            </div>

            <div className="d-flex gap-2 mt-4">

              <button
                className="btn btn-primary"
                onClick={handleSubmit}
              >
                Save
              </button>

              <button
                className="btn btn-secondary"
                onClick={() =>
                  setFormData(emptyForm())
                }
              >
                Reset
              </button>

            </div>
          </div>
        </div>

        {/* DATA TABLE */}
        <DataTable<StoneType>
          title="Stone Types"
          rows={items}
          rowKey={(r) =>
            r.stoneTypeID.toString()
          }
          searchPlaceholder="Search stone types..."
          exportFileName="stone-types"
          columns={[
            {
              key: "stoneTypeName",
              label: "Name",
              sortable: true,

              render: (r) => (
                <span className="fw-semibold">
                  {r.stoneTypeName}
                </span>
              ),
            },

            {
              key: "stoneTypeSlug",
              label: "Slug",

              render: (r) => (
                <code className="text-muted small">
                  {r.stoneTypeSlug}
                </code>
              ),
            },

            {
              key: "status",
              label: "Status",

              render: (r) => (
                <span
                  className={`badge ${
                    r.status === "Active"
                      ? "bg-success"
                      : "bg-secondary"
                  }`}
                >
                  {r.status}
                </span>
              ),
            },

            {
              key: "createdDate",
              label: "Created Date",

              render: (r) => (
                <span>
                  {r.createdDate
                    ? new Date(
                        r.createdDate
                      ).toLocaleDateString(
                        "en-GB"
                      )
                    : "-"}
                </span>
              ),
            },

            ...(canManage
              ? [
                  {
                    key: "actions" as const,
                    label: "Actions",
                    className: "text-end",

                    render: (
                      row: Record<string, unknown>
                    ) => {

                      const r =
                        row as unknown as StoneType;

                      return (
                        <div className="pg-actions">

                          <button
                            type="button"
                            onClick={() =>
                              setSelected(r)
                            }
                            title="Edit"
                            className="pg-action-btn pg-action-view"
                          >
                            <svg
                              width="13"
                              height="13"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                            >
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              setDeleteConfirm(r)
                            }
                            title="Delete"
                            className="pg-action-btn pg-action-delete"
                          >
                            <svg
                              width="13"
                              height="13"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                            >
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                              <path d="M10 11v6" />
                              <path d="M14 11v6" />
                            </svg>
                          </button>

                        </div>
                      );
                    },
                  },
                ]
              : []),
          ]}
        />
      </div>

      {/* DETAIL PANEL */}
      {selected && (
        <DetailPanel
          item={selected}
          onClose={() => setSelected(null)}
          onSaved={loadAll}
          onDeleted={loadAll}
          canManage={canManage}
        />
      )}

      {/* DELETE CONFIRM */}
      {deleteConfirm && (
        <div
          className="pm-del-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setDeleteConfirm(null);
            }
          }}
        >
          <div className="pm-del-box">

            <div className="pm-del-ico">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#ef4444"
                strokeWidth="2.5"
              >
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                <path d="M10 11v6" />
                <path d="M14 11v6" />
              </svg>
            </div>

            <div className="pm-del-title">
              Delete Stone Type?
            </div>

            <div className="pm-del-sub">
              This action cannot be undone.
            </div>

            <div className="pm-del-btns">

              <button
                className="pm-btn-ghost"
                onClick={() =>
                  setDeleteConfirm(null)
                }
                type="button"
                disabled={deleting}
              >
                Cancel
              </button>

              <button
                className="pm-btn-danger"
                onClick={doDelete}
                type="button"
                disabled={deleting}
              >
                {deleting
                  ? "Deleting..."
                  : "Delete"}
              </button>

            </div>
          </div>
        </div>
      )}

      {/* DELETE SUCCESS */}
      {deleteSuccessMsg && (
        <div
          className="pm-del-overlay"
          onClick={() =>
            setDeleteSuccessMsg(null)
          }
        >
          <div className="pm-del-box">

            <div
              className="pm-del-ico"
              style={{ background: "#f0fdf4" }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#22c55e"
                strokeWidth="2.5"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>

            <div
              className="pm-del-title"
              style={{ color: "#166534" }}
            >
              {deleteSuccessMsg}
            </div>

            <div className="pm-del-btns">
              <button
                className="pm-btn-ghost"
                onClick={() =>
                  setDeleteSuccessMsg(null)
                }
                type="button"
              >
                OK
              </button>
            </div>

          </div>
        </div>
      )}

      {/* INFO MODAL */}
      {infoModal && (
        <div
          className="pm-del-overlay"
          onClick={() =>
            setInfoModal(null)
          }
        >
          <div className="pm-del-box">

            <div
              className="pm-del-ico"
              style={{
                background:
                  infoModal.type === "success"
                    ? "#f0fdf4"
                    : infoModal.type === "warn"
                    ? "#fefce8"
                    : "#fef2f2",
              }}
            >

              {infoModal.type === "success" && (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#22c55e"
                  strokeWidth="2.5"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}

              {infoModal.type === "warn" && (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#eab308"
                  strokeWidth="2.5"
                >
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              )}

              {infoModal.type === "error" && (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#ef4444"
                  strokeWidth="2.5"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
              )}

            </div>

            <div
              className="pm-del-title"
              style={{
                color:
                  infoModal.type === "success"
                    ? "#166534"
                    : infoModal.type === "warn"
                    ? "#854d0e"
                    : "#991b1b",
              }}
            >
              {infoModal.msg}
            </div>

            <div className="pm-del-btns">
              <button
                className="pm-btn-ghost"
                onClick={() =>
                  setInfoModal(null)
                }
                type="button"
              >
                OK
              </button>
            </div>

          </div>
        </div>
      )}
    </>
  );
}